import React, { useEffect, useRef, useState } from 'react';

import ActionButtons from './ActionButtons';
import CMSection from './CMSection';
import CommercialSection from './CommercialSection';
import CostingSummary from './CostingSummary';
import EmbroideryTable from './EmbroideryTable';
import FabricSustainabilityTable from './FabricSustainabilityTable';
import FabricTable from './FabricTable';
import PackagingSection from './PackagingSection';
import PrintTable from './PrintTable';
import ProductWisePriceRange from './ProductWisePriceRange';
import TestingSection from './TestingSection';
import TrimsSection from './TrimsSection';
import WashTable from './WashTable';

const SECTION_HIGHLIGHT_DURATION = 1500;

const CostingTableAndSummary = ({
	payload = {},
	responsePayload = {},
	updateFormData = () => {},
	isEditable,
	validationErrors,
	reCostIndividually = () => {},
	trackChangedField = () => {},
	hasChanges,
	handleSubmit = () => {},
	hasSubmittedCosting,
	onReCost,
	historyId,
	isLiked,
	handleLikeUnlike = () => {},
	isLikeUnlikeLoading,
	costingBrandInfo,
	priceRangeData,
	isPriceRangeLoading,
	productInfoData,
	showActionButtons = true
}) => {
	const sectionRefs = useRef({});
	const [activeSection, setActiveSection] = useState(null);

	useEffect(() => {
		if (!activeSection) {
			return;
		}
		const timer = setTimeout(() => setActiveSection(null), SECTION_HIGHLIGHT_DURATION);

		return () => clearTimeout(timer);
	}, [activeSection]);

	// .costing-section has scroll-margin-top, so the sticky header never covers the section
	const scrollToSection = (sectionKey) => {
		sectionRefs.current[sectionKey]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		setActiveSection(sectionKey);
	};

	const sectionProps = (sectionKey) => ({
		ref: (node) => (sectionRefs.current[sectionKey] = node),
		className: `costing-section${activeSection === sectionKey ? ' costing-section-active' : ''}`
	});

	return (
		<div className="costing-table-and-summary d-flex flex-wrap gap-3">
			<div className="all-costing-table mb-4">
				{payload?.fiberRequirementList &&
					Array.isArray(payload?.fiberRequirementList) &&
					payload?.fiberRequirementList.length > 0 && (
						<div {...sectionProps('fiberRequirement')}>
							<FabricSustainabilityTable
								data={payload?.fiberRequirementList}
								onChange={(data) => updateFormData('fiberRequirementList', data)}
								isEditable={isEditable}
							/>
						</div>
					)}

				<div {...sectionProps('fabricCost')}>
					<FabricTable
						data={payload?.fabricWisePriceBreakdownList}
						cadConsumptions={payload?.cadWiseConsumptionList}
						fabricWiseMeasurements={payload?.fabricWiseMeasurements}
						totalCost={payload?.fabricCost || 0}
						measurementSizeRange={payload?.measurementSizeRange || ''}
						selectedMeasurementSize={payload?.selectedMeasurementSize || ''}
						onChange={(data) => updateFormData('fabricWisePriceBreakdownList', data)}
						onMeasurementsChange={(data) => updateFormData('fabricWiseMeasurements', data)}
						onConsumptionTypeChange={(data) => updateFormData('selectedConsumptionType', data)}
						isEditable={isEditable}
						validationErrors={validationErrors}
						onReCost={reCostIndividually}
						onFieldChange={trackChangedField}
						selectedConsumptionType={payload?.selectedConsumptionType || ''}
					/>
				</div>
				<div {...sectionProps('printCost')}>
					<PrintTable
						data={payload?.printPriceList}
						totalCost={payload?.printCost || 0}
						onChange={(data) => updateFormData('printPriceList', data)}
						isEditable={isEditable}
						validationErrors={validationErrors}
						onFieldChange={trackChangedField}
					/>
				</div>
				<div {...sectionProps('embroideryCost')}>
					<EmbroideryTable
						data={payload?.embroideryPriceList}
						totalCost={payload?.embroideryCost || 0}
						onChange={(data) => updateFormData('embroideryPriceList', data)}
						isEditable={isEditable}
						validationErrors={validationErrors}
						onFieldChange={trackChangedField}
					/>
				</div>
				<div {...sectionProps('washCost')}>
					<WashTable
						data={payload?.washPriceBreakdown}
						totalCost={payload?.washCost || 0}
						onChange={(data) => updateFormData('washPriceBreakdown', data)}
						isEditable={isEditable}
						validationErrors={validationErrors}
						onFieldChange={trackChangedField}
					/>
				</div>
				<div {...sectionProps('trimsCost')}>
					<TrimsSection
						data={payload?.trimsDetailsList}
						totalCost={payload?.trimsCost || 0}
						onChange={(data) => updateFormData('trimsDetailsList', data)}
						trimsAdditionalCharge={payload?.trimsAdditionalCharge}
						onTrimsAdditionalChargeChange={(value) =>
							updateFormData('trimsAdditionalCharge', value)
						}
						isEditable={isEditable}
						onFieldChange={trackChangedField}
					/>
				</div>
				<div {...sectionProps('packaging')}>
					<PackagingSection
						data={payload?.packagingPriceCostBreakdown}
						totalCost={payload?.packagingCost || 0}
						onChange={(data) => updateFormData('packagingPriceCostBreakdown', data)}
						isEditable={isEditable}
						onFieldChange={trackChangedField}
					/>
				</div>
				<div {...sectionProps('cmCost')}>
					<CMSection
						value={payload?.cmCost}
						totalCost={responsePayload?.cmCost || payload?.cmCost || 0}
						onChange={(value) => updateFormData('cmCost', value)}
						isEditable={isEditable}
						onFieldChange={trackChangedField}
					/>
				</div>
				<div {...sectionProps('perPisTestingCost')}>
					<TestingSection
						perStyleTestingCost={payload?.perStyleTestingCost}
						totalCost={payload?.perPisTestingCost || 0}
						onChange={(value) => updateFormData('perStyleTestingCost', value)}
						isEditable={isEditable}
						onFieldChange={trackChangedField}
					/>
				</div>
				<div {...sectionProps('commercial')}>
					<CommercialSection
						value={payload?.commercialCostPercentage}
						totalCost={payload?.commercialCost || 0}
						onChange={(value) => updateFormData('commercialCostPercentage', value)}
						isEditable={isEditable}
						onFieldChange={trackChangedField}
					/>
				</div>
			</div>
			<div className="costing-summary-container">
				{showActionButtons && (
					<ActionButtons
						hasChanges={hasChanges}
						onSubmit={handleSubmit}
						isEditable={isEditable}
						hasSubmittedCosting={hasSubmittedCosting}
						onReCost={onReCost}
					/>
				)}
				<CostingSummary
					summary={{
						fabricConsumption: payload?.estimatedConsumption || 0,
						fabricCost: payload?.fabricCost || 0,
						printCost: payload?.printCost || 0,
						embroideryCost: payload?.embroideryCost || 0,
						trimsCost: payload?.trimsCost || 0,
						cmCost: responsePayload?.cmCost || payload?.cmCost || 0,
						washCost: payload?.washCost || 0,
						packaging: payload?.packagingCost || 0,
						commercial: payload?.commercialCost || 0,
						perPisTestingCost: payload?.perPisTestingCost || 0
					}}
					historyId={historyId}
					isLiked={isLiked}
					onLike={() => handleLikeUnlike(true)}
					onDislike={() => handleLikeUnlike(false)}
					isLoading={isLikeUnlikeLoading}
					totalFobPerUnit={payload?.predictedCost || 0}
					baseQty={payload?.quantity}
					costingBrandInfo={costingBrandInfo}
					quantityWisePrices={payload?.qtyWisePriceList || []}
					onSectionClick={scrollToSection}
				/>
				<ProductWisePriceRange
					priceRangeData={priceRangeData?.payload || {}}
					isPriceRangeLoading={isPriceRangeLoading}
					predictedCost={payload?.predictedCost || 0}
					category={productInfoData?.subCategoryResponse?.name}
				/>
			</div>
		</div>
	);
};

export default CostingTableAndSummary;
